import React, { useState, useEffect, useMemo } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    IconButton,
    Grid,
    CircularProgress,
    MenuItem
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useGetTask, useUpdateTask } from 'services/api/task-management/trackerHooks'
import { format, parseISO } from 'date-fns'
import { VariantElement } from '../../constant/card';
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import TaskWorkingPeriodPicker from 'module/task-management/components/TaskWorkingPeriodPicker'

const buildEditTaskSnapshot = (formData) => ({
    taskName: String(formData?.taskName || '').trim(),
    deliverableTypeId: String(formData?.deliverableTypeId || ''),
    deliverableValue: String(formData?.deliverableValue || '').trim(),
    dueDate: formData?.dueDate ? format(formData.dueDate, 'yyyy-MM-dd') : null,
    workingPeriodStart: formData?.workingPeriodStart ? format(formData.workingPeriodStart, 'yyyy-MM-dd') : null,
    workingPeriodEnd: formData?.workingPeriodEnd ? format(formData.workingPeriodEnd, 'yyyy-MM-dd') : null
})

const EditTaskModal = ({ open, onClose, taskData, onSave }) => {
    // Extract task ID from taskData
    const taskId = taskData?.taskId || taskData?.id

    // Fetch full task details from API
    const {
        data: taskDetails,
        isLoading,
        isError
    } = useGetTask(taskId, {
        enabled: open && !!taskId // Only fetch when modal is open and taskId exists
    })

    // Update task mutation
    const { mutate: updateTask, isPending: isUpdating, isError: isUpdateError, error: updateError } = useUpdateTask()

    const [formData, setFormData] = useState({
        project: '',
        taskName: '',
        deliverableType: '',
        deliverableTypeId: '', // Store ID for backend
        deliverableValue: '',
        targetValue: '',
        remainingValue: '',
        dueDate: null,
        workingPeriodStart: null,
        workingPeriodEnd: null
    })

    const [formErrors, setFormErrors] = useState({})
    const [showDiscardModal, setShowDiscardModal] = useState(false)
    const [initialSnapshot, setInitialSnapshot] = useState(null)

    // Store available deliverables from project
    const [availableDeliverables, setAvailableDeliverables] = useState([])

    const currentSnapshot = useMemo(() => buildEditTaskSnapshot(formData), [formData])
    const isDirty = useMemo(() => {
        if (!initialSnapshot) return false
        return JSON.stringify(currentSnapshot) !== JSON.stringify(initialSnapshot)
    }, [currentSnapshot, initialSnapshot])

    const resetForm = () => {
        setFormData({
            project: '',
            taskName: '',
            deliverableType: '',
            deliverableTypeId: '',
            deliverableValue: '',
            targetValue: '',
            remainingValue: '',
            dueDate: null,
            workingPeriodStart: null,
            workingPeriodEnd: null
        })
        setAvailableDeliverables([])
        setProjectDateRange({
            startDate: null,
            endDate: null
        })
        setFormErrors({})
    }

    // Populate form when taskDetails are loaded
    useEffect(() => {
        if (taskDetails?.data?.task && open) {
            const task = taskDetails.data.task

            if (task.project_member_deliverable) {
                setAvailableDeliverables([{
                    id: task.project_member_deliverable_id,
                    deliverable_type: task.project_member_deliverable.deliverable_type,
                    target_value: task.project_member_deliverable.target_value,
                    remaining_value: task.project_member_deliverable.remaining_value,
                    overflow_allowed: task.project_member_deliverable.overflow_allowed
                }])
            }

            // extract project date range
            if (task.project) {
                setProjectDateRange({
                    startDate: task.project.start_date ? parseISO(task.project.start_date) : null,
                    endDate: task.project.deadline ? parseISO(task.project.deadline) : null
                })
            }

            setFormData({
                project: task.project?.name || '',
                taskName: task.name || '',
                deliverableType: task.project_member_deliverable?.deliverable_type || '',
                deliverableTypeId: task.project_member_deliverable_id || '', // Store the ID for backend
                deliverableValue: task.value || '',
                targetValue: task.project_member_deliverable?.target_value || '',
                remainingValue: task.project_member_deliverable?.remaining_value || '',
                dueDate: task.due_date ? new Date(task.due_date) : null,
                workingPeriodStart: task.scheduled_date ? new Date(task.scheduled_date) : null,
                workingPeriodEnd: task.scheduled_end_date ? new Date(task.scheduled_end_date) : null
            })
            setInitialSnapshot(buildEditTaskSnapshot({
                taskName: task.name || '',
                deliverableTypeId: task.project_member_deliverable_id || '',
                deliverableValue: task.value || '',
                dueDate: task.due_date ? new Date(task.due_date) : null,
                workingPeriodStart: task.scheduled_date ? new Date(task.scheduled_date) : null,
                workingPeriodEnd: task.scheduled_end_date ? new Date(task.scheduled_end_date) : null
            }))
            setShowDiscardModal(false)
            setFormErrors({})
        }
    }, [taskDetails, open])

    useEffect(() => {
        if (!open) {
            resetForm()
            setInitialSnapshot(null)
            setShowDiscardModal(false)
        }
    }, [open])

    // Store project date range for validation
    const [projectDateRange, setProjectDateRange] = useState({
        startDate: null,
        endDate: null
    })

    const handleChange = (field) => (event) => {
        setFormData({
            ...formData,
            [field]: event.target.value
        })
    }

    const handleDeliverableTypeChange = (event) => {
        const selectedId = event.target.value
        const selected = availableDeliverables.find(d => d.id === parseInt(selectedId))

        setFormData({
            ...formData,
            deliverableTypeId: selectedId,
            deliverableType: selected?.deliverable_type || '',
            targetValue: selected?.target_value || '',
            remainingValue: selected?.remaining_value || ''
        })
    }

    const handleDateChange = (field) => (date) => {
        setFormData({ ...formData, [field]: date })
        if (formErrors[field]) {
            setFormErrors((prev) => ({ ...prev, [field]: '' }))
        }
    }

    const validateForm = () => {
        const nextErrors = {}

        if (!String(formData.taskName || '').trim()) {
            nextErrors.taskName = 'Task name is required'
        }

        if (!formData.deliverableTypeId) {
            nextErrors.deliverableTypeId = 'Deliverable type is required'
        }

        if (!formData.deliverableValue || Number(formData.deliverableValue) <= 0) {
            nextErrors.deliverableValue = 'Deliverable value must be greater than 0'
        }

        if (!formData.workingPeriodStart) {
            nextErrors.workingPeriodStart = 'Working period start date is required'
        }

        if (!formData.workingPeriodEnd) {
            nextErrors.workingPeriodEnd = 'Working period end date is required'
        }

        if (!formData.dueDate) {
            nextErrors.dueDate = 'Due date is required'
        }

        if (
            formData.workingPeriodStart &&
            formData.workingPeriodEnd &&
            formData.workingPeriodStart > formData.workingPeriodEnd
        ) {
            nextErrors.workingPeriodEnd = 'Working period end date must be on or after the start date'
        }

        if (formData.workingPeriodEnd && formData.dueDate && formData.workingPeriodEnd > formData.dueDate) {
            nextErrors.workingPeriodEnd = 'Working period end date must be on or before the due date'
        }

        if (projectDateRange.startDate && formData.workingPeriodStart && formData.workingPeriodStart < projectDateRange.startDate) {
            nextErrors.workingPeriodStart = `Working period start date must be on or after ${format(projectDateRange.startDate, 'MMM dd, yyyy')}`
        }

        if (projectDateRange.endDate && formData.workingPeriodEnd && formData.workingPeriodEnd > projectDateRange.endDate) {
            nextErrors.workingPeriodEnd = `Working period end date must be on or before ${format(projectDateRange.endDate, 'MMM dd, yyyy')}`
        }

        if (projectDateRange.startDate && formData.dueDate && formData.dueDate < projectDateRange.startDate) {
            nextErrors.dueDate = `Due date must be on or after ${format(projectDateRange.startDate, 'MMM dd, yyyy')}`
        }

        if (projectDateRange.endDate && formData.dueDate && formData.dueDate > projectDateRange.endDate) {
            nextErrors.dueDate = `Due date must be on or before ${format(projectDateRange.endDate, 'MMM dd, yyyy')}`
        }

        setFormErrors(nextErrors)
        return Object.keys(nextErrors).length === 0
    }

    const handleSave = () => {
        if (!taskId || !taskDetails?.data?.task) return
        if (!validateForm()) return

        const task = taskDetails.data.task

        // Validate value with overflow check
        const deliverable = task.project_member_deliverable
        if (deliverable && formData.deliverableValue) {
            const enteredValue = parseFloat(formData.deliverableValue)
            const currentTaskValue = parseFloat(task.value || 0)
            const remainingValue = parseFloat(deliverable.remaining_value)
            const overflowAllowed = deliverable.overflow_allowed

            // Calculate max allowed (add back current task value)
            const maxAllowed = remainingValue + currentTaskValue

            // Only validate if overflow is NOT allowed
            if (!overflowAllowed && enteredValue > maxAllowed) {
                console.error(`Value cannot exceed ${maxAllowed}. Overflow is not allowed.`)
                return // Block submission
            }
        }

        // Prepare payload according to backend API
        const payload = {
            project_id: task.project_id,
            project_member_deliverable_id: parseInt(formData.deliverableTypeId), // Use the stored ID
            name: formData.taskName,
            value: parseFloat(formData.deliverableValue),
            due_date: formData.dueDate ? format(formData.dueDate, 'yyyy-MM-dd') : null,
            scheduled_date: formData.workingPeriodStart ? format(formData.workingPeriodStart, 'yyyy-MM-dd') : null,
            scheduled_end_date: formData.workingPeriodEnd ? format(formData.workingPeriodEnd, 'yyyy-MM-dd') : null
        }

        // Call the update mutation
        updateTask(
            { taskId, taskData: payload },
            {
                onSuccess: (data) => {
                    // Call parent onSave callback if provided
                    if (onSave) {
                        onSave(data)
                    }
                    finalClose()
                },
                onError: (error) => {
                    console.error('Failed to update task:', error)
                    // Error will be shown in the UI via isUpdateError
                }
            }
        )
    }

    const finalClose = () => {
        setShowDiscardModal(false)
        setInitialSnapshot(null)
        resetForm()
        onClose()
    }

    const requestClose = (source, reason) => {
        if (isUpdating) return
        if (reason === 'backdropClick') return

        if (!isDirty) {
            finalClose()
            return
        }

        setShowDiscardModal(true)
    }

    return (
        <>
        <Dialog
            open={open}
            onClose={(event, reason) => requestClose('modal', reason)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2.5, maxWidth: '600px' }
            }}
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={2}>
                        <Box
                            sx={{
                                width: 45,
                                height: 45,
                                borderRadius: '100%',
                                backgroundColor: '#deecff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M7 17.0134L11.413 16.9984L21.045 7.45839C21.423 7.08039 21.631 6.57839 21.631 6.04439C21.631 5.51039 21.423 5.00839 21.045 4.63039L19.459 3.04439C18.703 2.28839 17.384 2.29239 16.634 3.04139L7 12.5834V17.0134ZM18.045 4.45839L19.634 6.04139L18.037 7.62339L16.451 6.03839L18.045 4.45839ZM9 13.4174L15.03 7.44439L16.616 9.03039L10.587 15.0014L9 15.0064V13.4174Z" fill="#2666BE" />
                                <path d="M5 21H19C20.103 21 21 20.103 21 19V10.332L19 12.332V19H8.158C8.132 19 8.105 19.01 8.079 19.01C8.046 19.01 8.013 19.001 7.979 19H5V5H11.847L13.847 3H5C3.897 3 3 3.897 3 5V19C3 20.103 3.897 21 5 21Z" fill="#2666BE" />
                            </svg>
                        </Box>
                        <Typography variant="h3" fontSize="20px" fontWeight={500}>
                            Edit Task
                        </Typography>
                    </Box>
                    <IconButton onClick={() => requestClose('icon')} size="small" sx={{ color: '#3a3a3a' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers sx={{ py: 3 }}>
                {/* Loading State */}
                {isLoading && (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                        <CircularProgress />
                    </Box>
                )}

                {/* Error State - Loading */}
                {isError && (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                        <Typography color="error">Failed to load task details. Please try again.</Typography>
                    </Box>
                )}

                {/* Error State - Update */}
                {isUpdateError && (
                    <Box mb={2} p={2} bgcolor="#ffebee" borderRadius={1}>
                        <Typography color="error" fontSize="14px">
                            {updateError?.response?.data?.message || 'Failed to update task. Please check your inputs and try again.'}
                        </Typography>
                    </Box>
                )}

                {/* Form Content - Only show when data is loaded */}
                {!isLoading && !isError && taskDetails && (
                    <>
                        {/* Project - Read Only Display */}
                        <Box mb={3}>
                            <Typography variant="body2" fontWeight={500} mb={1}>
                                Project
                            </Typography>
                            <TextField
                                fullWidth
                                value={formData.project}
                                disabled
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5,
                                        backgroundColor: '#f5f5f5'
                                    }
                                }}
                            />
                        </Box>

                        {/* Task Name */}
                        <Box mb={3}>
                            <Typography variant="body2" fontWeight={500} mb={1}>
                                Task Name <VariantElement component="span" variant="redAlert">*</VariantElement>
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Enter task name"
                                value={formData.taskName}
                                onChange={handleChange('taskName')}
                                inputProps={{ maxLength: 45 }}
                                error={!!formErrors.taskName}
                                helperText={formErrors.taskName || `${formData.taskName.length}/45`}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5
                                    }
                                }}
                            />
                        </Box>

                        {/* Deliverable Type and Value */}
                        <Box mb={3}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" fontWeight={500} mb={1}>
                                        Deliverable Type <VariantElement component="span" variant="redAlert">*</VariantElement>
                                    </Typography>
                                    <TextField
                                        select
                                        fullWidth
                                        value={formData.deliverableTypeId}
                                        onChange={handleDeliverableTypeChange}
                                        error={!!formErrors.deliverableTypeId}
                                        helperText={formErrors.deliverableTypeId}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5
                                            }
                                        }}
                                    >
                                        {availableDeliverables.map((deliverable) => (
                                            <MenuItem key={deliverable.id} value={deliverable.id}>
                                                {deliverable.deliverable_type}
                                                {' '}(Remaining: {deliverable.remaining_value})
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography variant="body2" fontWeight={500} mb={1}>
                                        Deliverable Value <VariantElement component="span" variant="redAlert">*</VariantElement>
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        placeholder="Enter deliverable value"
                                        value={formData.deliverableValue}
                                        onChange={handleChange('deliverableValue')}
                                        error={!!formErrors.deliverableValue}
                                        helperText={formErrors.deliverableValue}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5
                                            }
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        You have remaining {formData.remainingValue || '0'} credit value
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Working Period */}
                        <Box mt={3} mb={3}>
                            <Typography variant="body2" fontWeight={500} mb={1}>
                                Working Period <VariantElement component="span" variant="redAlert">*</VariantElement>
                            </Typography>
                            <TaskWorkingPeriodPicker
                                label=""
                                name="workingPeriod"
                                required
                                startDate={formData.workingPeriodStart}
                                endDate={formData.workingPeriodEnd}
                                projectStartDate={projectDateRange.startDate}
                                projectDeadline={projectDateRange.endDate}
                                dueDate={formData.dueDate}
                                disabled={isUpdating}
                                disablePast={false}
                                startError={formErrors.workingPeriodStart}
                                endError={formErrors.workingPeriodEnd}
                                onChange={({ startDate, endDate }) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        workingPeriodStart: startDate,
                                        workingPeriodEnd: endDate,
                                    }))

                                    if (formErrors.workingPeriodStart || formErrors.workingPeriodEnd) {
                                        setFormErrors((prev) => ({
                                            ...prev,
                                            workingPeriodStart: '',
                                            workingPeriodEnd: '',
                                        }))
                                    }
                                }}
                            />
                        </Box>

                        {/* Due date */}
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} mb={3}>
                                    <Typography variant="body2" fontWeight={500} mb={1}>
                                        Due date <VariantElement component="span" variant="redAlert">*</VariantElement>
                                    </Typography>
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                            value={formData.dueDate}
                                            onChange={handleDateChange('dueDate')}
                                            format="dd/MM/yyyy"
                                            minDate={formData.workingPeriodEnd || formData.workingPeriodStart || projectDateRange.startDate || undefined}
                                            maxDate={projectDateRange.endDate || undefined}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    placeholder: 'DD/MM/YYYY',
                                                    error: !!formErrors.dueDate,
                                                    helperText: formErrors.dueDate,
                                                    sx: {
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 1.5
                                                        },
                                                        '& .MuiFormHelperText-root': {
                                                            mx: 0
                                                        }
                                                    }
                                                }
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                            </Grid>
                        </Box>

                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} 
                            sx={{
                                borderLeft: '6px solid #F3BF3A',
                                backgroundColor: '#FDF6E2',
                                borderRadius: '4.5px',
                                p: '10px'
                            }}>
                            <TipsAndUpdatesOutlinedIcon />
                            <Typography>
                                You can reschedule your planned dates later from the Work Planner or Gantt view by updating the working period.
                            </Typography>
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2.5, gap: 1, bgcolor: '#FBF5E2' }}>
                <Button
                    variant="outlined"
                    onClick={() => requestClose('cancel')}
                    disabled={isUpdating}
                    sx={{
                        textTransform: 'none',
                        color: 'text.primary',
                        fontSize: '14px',
                        fontWeight: 500,
                        px: '16px',
                        py: '10px',
                        minWidth: '150px',
                        border: '1px solid #F3BF3A',
                        borderRadius: '100px',
                        '&:hover': {
                            backgroundColor: '#FDF6E2'
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={
                        !formData.taskName ||
                        !formData.deliverableType ||
                        !formData.deliverableValue ||
                        !formData.dueDate ||
                        !formData.workingPeriodStart ||
                        !formData.workingPeriodEnd ||
                        isUpdating
                    }
                    startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#F3BF3A',
                        px: '14px',
                        py: '12px',
                        fontSize: '14px',
                        fontWeight: 500,
                        borderRadius: '100px',
                        boxShadow: 'none',
                        '&:hover': { backgroundColor: '#D09A01', boxShadow: 'none' },
                        '&:disabled': {
                            backgroundColor: '#E0E0E0',
                            color: '#9E9E9E',
                            boxShadow: 'none'
                        }
                    }}
                >
                    {isUpdating ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
        <AdxInfoModal
            variant="error"
            open={showDiscardModal}
            onClose={() => setShowDiscardModal(false)}
            muiIcon="warning"
            iconColor="error.main"
            title="Discard changes?"
            subtitle="Any unsaved changes will be discarded"
            footerButtons
            firstBtnLabel="Discard"
            firstBtnHandle={finalClose}
            secondBtnLabel="Continue editing"
            secondBtnHandle={() => setShowDiscardModal(false)}
        />
        </>
    )
}

export default EditTaskModal
